import React, { useState, useEffect, useRef } from "react";
import axiosApi from "../server/service";

// --- ICONS ---
import { Mic, StopCircle, UploadCloud, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { c } from "framer-motion/dist/types.d-Cjd591yU";

// --- TYPE DEFINITIONS ---
interface IVoiceNote {
    url: string;
    uploadedAt: string;
    _id: string;
}

// --- Custom Hook for handling the browser's MediaRecorder API (omitted - unchanged) ---
const useVoiceRecorder = () => {
    // ... (omitted - logic remains unchanged)
    const [isRecording, setIsRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
    const [recordingTime, setRecordingTime] = useState(0);
    const [error, setError] = useState<string | null>(null);


    
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);



    const startRecording = async () => {
        setError(null);
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
                mediaRecorderRef.current = mediaRecorder;
                
                const audioChunks: Blob[] = [];
                mediaRecorder.ondataavailable = (event) => {
                    audioChunks.push(event.data);
                };

                mediaRecorder.onstop = () => {
                    const blob = new Blob(audioChunks, { type: 'audio/webm' });
                    setAudioBlob(blob);
                    stream.getTracks().forEach(track => track.stop()); // Important: release the microphone
                };

                mediaRecorder.start();
                setIsRecording(true);
                timerIntervalRef.current = setInterval(() => {
                    setRecordingTime(prev => prev + 1);
                }, 1000);

            } catch (err) {
                console.error("Microphone access denied:", err);
                setError("Microphone access is required to record audio.");
            }
        } else {
             setError("Audio recording is not supported by this browser.");
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            if(timerIntervalRef.current) clearInterval(timerIntervalRef.current);
            setRecordingTime(0);
        }
    };

    const resetRecording = () => {
        setAudioBlob(null);
        setIsRecording(false);
        setRecordingTime(0);
        if(timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
    
    const formatTime = (timeInSeconds: number) => {
        const minutes = Math.floor(timeInSeconds / 60).toString().padStart(2, '0');
        const seconds = (timeInSeconds % 60).toString().padStart(2, '0');
        return `${minutes}:${seconds}`;
    };

    return { isRecording, audioBlob, recordingTime: formatTime(recordingTime), error, startRecording, stopRecording, resetRecording };
};


interface VoiceNoteRecorderProps {
  orderId?: string;
  orderDetailId?: string;
  existingNotes: IVoiceNote[]; 
  onUpdate: () => void; 
  uploadUrl?: string; // Optional for backward compatibility
  onUpload?: (audioBlob: Blob) => Promise<any>; // For custom upload logic
  isReadOnly?: boolean; // To disable recording controls
}

const VoiceNoteRecorder: React.FC<VoiceNoteRecorderProps> = ({ 
    orderId, 
    orderDetailId, 
    existingNotes, 
    onUpdate, 
    uploadUrl, 
    onUpload,
    isReadOnly 
}) => {
    const { isRecording, audioBlob, recordingTime, error, startRecording, stopRecording, resetRecording } = useVoiceRecorder();
const [uploading, setUploading] = useState(false);
 const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

     const [voiceNotes, setVoiceNotes] = useState<IVoiceNote[]>(existingNotes);

         useEffect(() => {
  setVoiceNotes(existingNotes);
}, [existingNotes]);

    // console.log("VoiceNoteRecorder existingNotes:", existingNotes);
    // console.log("VoiceNoteRecorder voiceNotes state:", voiceNotes);



    // ✅ UPDATE this function to be more flexible
    const handleUpload = async () => {
        if (!audioBlob) return;
        setUploading(true);
        setMessage(null);

        try {
            // Priority 1: Use the custom onUpload function if provided
            if (onUpload) {
                await onUpload(audioBlob);
                // The parent component handles success/error toasts and data refresh
            } 
            // Priority 2: Use the uploadUrl if provided (for employee dashboard)
            else if (uploadUrl) {
                const formData = new FormData();
                formData.append("audio", audioBlob, `vn-order-${orderId}-seg-${orderDetailId}.webm`);
                
                 const { data } = await axiosApi.post(uploadUrl, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });

                 if (data?.note) {
                    // ✅ Immediately add new note and keep list visible
                    setVoiceNotes((prev) => [...prev, data.note]);
                    }

                    setMessage({ type: 'success', text: "Upload successful!" });
                    // onUpdate();

                    // ✅ Reset only the recording controls, not voice note visibility
                    setTimeout(() => {
                    resetRecording();
                    }, 500);

           
            } 
            else {
                throw new Error("No upload destination specified.");
            }
            resetRecording();
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || "Upload failed.";
            setMessage({ type: 'error', text: errorMessage });
        } finally {
            setUploading(false);
        }
    };


    return (
     <div className="w-full space-y-4">
        {/* --- RECORDING SECTION --- */}
        {!isReadOnly && (
            <div>
                <label className="font-semibold text-slate-700">Record New Voice Note</label>
                {error && <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded-lg flex items-center"><AlertTriangle size={16} className="mr-2"/> {error}</div>}
                
                <div className="mt-2 flex items-center gap-4">
                    <div className="flex-grow">
                        {!audioBlob && !isRecording && (
                            <button onClick={startRecording} className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold bg-white border-2 border-slate-300 text-slate-600 hover:bg-slate-50 transition">
                                <Mic size={20} /><span>Start Recording</span>
                            </button>
                        )}
                        {isRecording && (
                            <button onClick={stopRecording} className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold bg-red-600 text-white border-2 border-red-600 hover:bg-red-700 transition">
                                <StopCircle size={20} /><span>Stop Recording ({recordingTime})</span>
                            </button>
                        )}
                        {audioBlob && !isRecording && (
                            <audio src={URL.createObjectURL(audioBlob)} controls className="w-full h-10" />
                        )}
                    </div>
                    {audioBlob && !isRecording && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <button onClick={handleUpload} disabled={uploading} className="p-3 rounded-lg bg-sky-600 text-white hover:bg-sky-700 disabled:bg-sky-300 transition flex items-center justify-center" title="Upload Recording">
                               {uploading ? <Loader2 size={20} className="animate-spin"/> : <UploadCloud size={20} />}
                            </button>
                            <button onClick={resetRecording} className="p-3 rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 transition" title="Clear Recording">
                                <Trash2 size={20}/>
                            </button>
                        </div>
                    )}
                </div>

                {message && <p className={`mt-2 text-sm font-semibold ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>{message.text}</p>}
            </div>
        )}

 {/* --- SAVED NOTES SECTION --- */}
{voiceNotes && voiceNotes.length > 0 && (
  <div className={!isReadOnly ? "border-t pt-4" : ""}>
      {/* <h4 className="font-semibold text-slate-700 mb-2">
        Saved Notes ({voiceNotes.length})
      </h4> */}
      <div className="space-y-3 max-h-48 overflow-y-auto pr-2">
        {voiceNotes.slice().reverse().map(note => (

        <div key={note._id} className="flex items-center gap-3 p-2 bg-slate-100 rounded-lg">
         <div className="flex-grow">
          <audio src={note.url} controls className="w-full h-10"></audio>
          <p className="text-xs text-slate-500 mt-1">
           Recorded on: {new Date(note.uploadedAt).toLocaleString()}
          </p>
         </div>
        </div>
       ))}
      </div>
     </div>
    )}
  </div>
);
};

export default VoiceNoteRecorder;